import React, { useState } from 'react';
import { X } from 'lucide-react';
import { SignUpData, LoginData, signUp, login, sendPasswordReset } from '../../lib/auth';
import { AuthInput } from './AuthInput';
import { AuthButton } from './AuthButton';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'login' | 'signup';
  onSuccess: () => void;
}

export function AuthModal({ isOpen, onClose, mode, onSuccess }: AuthModalProps) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'signup') {
        await signUp(formData as SignUpData);
        setSuccess('Account created! Please check your email for verification.');
      } else {
        await login(formData as LoginData);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      await sendPasswordReset(formData.email);
      setSuccess('Password reset email sent! Please check your inbox.');
      setShowResetPassword(false);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[200] p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
      <div 
        className="bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-lg w-full max-w-md 
          transition-all duration-300"
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl text-white font-playfair font-light">
              {showResetPassword ? 'Reset Password' : mode === 'signup' ? 'Create Account' : 'Welcome Back'}
            </h2>
            <button 
              onClick={onClose} 
              className="text-white/70 hover:text-white transition-colors p-1 hover:bg-white/10 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showResetPassword ? (
          <form onSubmit={handleResetPassword} className="p-6 space-y-4">
            {error && (
              <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-4 bg-green-500/10 backdrop-blur-sm text-green-200 rounded-xl text-sm">
                {success}
              </div>
            )}

            <AuthInput
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
            />

            <div className="flex gap-3 pt-2">
              <AuthButton
                type="button"
                variant="secondary"
                onClick={() => setShowResetPassword(false)}
              >
                Back to Login
              </AuthButton>
              <AuthButton
                type="submit"
                loading={loading}
                loadingText="Sending..."
              >
                Send Reset Link
              </AuthButton>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-4 bg-red-500/10 backdrop-blur-sm text-red-200 rounded-xl text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-4 bg-green-500/10 backdrop-blur-sm text-green-200 rounded-xl text-sm">
                {success}
              </div>
            )}

            {mode === 'signup' && (
              <AuthInput
                label="Name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
              />
            )}

            <AuthInput
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="your@email.com"
            />

            <AuthInput
              label="Password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              minLength={6}
            />

            {mode === 'login' && (
              <button
                type="button"
                onClick={() => setShowResetPassword(true)}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors duration-200"
              >
                Forgot password?
              </button>
            )}

            <AuthButton
              type="submit"
              loading={loading}
              loadingText={mode === 'signup' ? 'Creating Account...' : 'Logging In...'}
              className="mt-2"
            >
              {mode === 'signup' ? 'Create Account' : 'Log In'}
            </AuthButton>
          </form>
        )}
      </div>
    </div>
  );
}